import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Heart } from "lucide-react";
import { API_URL } from "@/config";

const TechPortalPage = () => {
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comments, setComments] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const res = await fetch(`${API_URL}/api/tickets/${ticketId}/details`);
        if (!res.ok) throw new Error('Ticket not found');
        const data = await res.json();
        setTicket(data);
        // Auto-select the next logical status
        const nextStatus = {
          'Dispatched': 'Dispatch Acknowledged',
          'Dispatch Acknowledged': 'En Route',
          'En Route': 'On Site',
          'On Site': 'Work Complete',
        };
        setSelectedStatus(nextStatus[data.status] || data.status);
        setComments(data.techComments || '');
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchTicket();
  }, [ticketId]);

  const handleSubmit = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tickets/${ticketId}/tech-update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: selectedStatus, techComments: comments })
      });
      if (!res.ok) throw new Error('Update failed');
      const updated = await res.json();
      setTicket(updated);
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="tech-portal" data-testid="tech-portal-loading">
        <div className="tech-portal-card">
          <p style={{color:'#888', textAlign:'center'}}>Loading ticket...</p>
        </div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="tech-portal" data-testid="tech-portal-error">
        <div className="tech-portal-card" style={{borderColor:'#ff4757'}}>
          <h1 className="tech-portal-title" style={{color:'#ff4757'}}>TICKET NOT FOUND</h1>
          <p style={{color:'#ccc', textAlign:'center'}}>Ticket <strong style={{color:'#ffd700'}}>{ticketId}</strong> does not exist or has been removed.</p>
          <p style={{color:'#888', textAlign:'center', fontSize:'13px', marginTop:'16px'}}>Please contact your dispatcher for assistance.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="tech-portal" data-testid="tech-portal-success">
        <div className="tech-portal-card" style={{borderColor:'#00ff88'}}>
          <div style={{fontSize:'48px', textAlign:'center', marginBottom:'16px', color:'#00ff88'}}>&#10003;</div>
          <h1 className="tech-portal-title" style={{color:'#00ff88'}}>UPDATE SUBMITTED</h1>
          <p style={{color:'#ccc', textAlign:'center', marginBottom:'8px'}}>Ticket <strong style={{color:'#ffd700'}}>{ticket.ticketId}</strong></p>
          <p style={{color:'#888', textAlign:'center', fontSize:'14px'}}>Status: <strong style={{color:'#00f0ff'}}>{ticket.status}</strong></p>
          {ticket.techComments && (
            <p style={{color:'#888', textAlign:'center', fontSize:'13px', marginTop:'12px'}}>Your comments have been recorded.</p>
          )}
          <button className="tech-portal-btn primary" onClick={() => setSubmitted(false)} style={{marginTop:'20px'}}>
            UPDATE AGAIN
          </button>
        </div>
      </div>
    );
  }

  const statusOptions = [
    { value: 'Dispatch Acknowledged', label: 'Acknowledge Dispatch' },
    { value: 'En Route', label: 'En Route to Site' },
    { value: 'On Site', label: 'On Site' },
    { value: 'Work Complete', label: 'Work Complete' },
  ];

  return (
    <div className="tech-portal" data-testid="tech-portal-page">
      <div className="tech-portal-card">
        <div className="tech-portal-header">
          <Heart size={24} fill="#ff4757" color="#ff4757" />
          <div>
            <div className="tech-portal-brand">
              <span style={{color:'#ff4757'}}>RESCUE</span><span style={{color:'#ffd700'}}>AID</span>
            </div>
            <div style={{color:'#888', fontSize:'11px', letterSpacing:'1px'}}>TECH PORTAL</div>
          </div>
        </div>

        <h1 className="tech-portal-title">SERVICE DISPATCH</h1>
        <div className="tech-portal-ticket-id">{ticket.ticketId}</div>

        <div className="tech-portal-status">
          <span className={`ticket-status-badge ${ticket.status.toLowerCase().replace(' ', '-')}`}>{ticket.status}</span>
          <span className={`ticket-priority-badge ${ticket.priority.toLowerCase()}`}>{ticket.priority}</span>
        </div>

        <div className="tech-portal-details">
          <div className="tech-portal-row"><span className="tp-label">Subscriber</span><span className="tp-value">{ticket.subscriberName}</span></div>
          <div className="tech-portal-row"><span className="tp-label">Device</span><span className="tp-value">{ticket.deviceId}</span></div>
          <div className="tech-portal-row"><span className="tp-label">Type</span><span className="tp-value">{ticket.deviceType}</span></div>
          <div className="tech-portal-row"><span className="tp-label">Issue</span><span className="tp-value" style={{color:'#ff4757'}}>{ticket.issueType}</span></div>
          <div className="tech-portal-row"><span className="tp-label">Location</span><span className="tp-value">{ticket.location}</span></div>
          <div className="tech-portal-row"><span className="tp-label">Model</span><span className="tp-value">{ticket.model || 'N/A'}</span></div>
          {ticket.dueDate && <div className="tech-portal-row"><span className="tp-label">Due Date</span><span className="tp-value">{ticket.dueDate}</span></div>}
          {ticket.description && <div className="tech-portal-row"><span className="tp-label">Description</span><span className="tp-value">{ticket.description}</span></div>}
          {ticket.notes && <div className="tech-portal-row"><span className="tp-label">Notes</span><span className="tp-value">{ticket.notes}</span></div>}
        </div>

        <div className="tech-portal-section">
          <label className="tech-portal-section-label">UPDATE STATUS</label>
          <div className="tech-portal-status-options">
            {statusOptions.map(opt => (
              <button
                key={opt.value}
                className={`tech-portal-status-btn ${selectedStatus === opt.value ? 'active' : ''}`}
                onClick={() => setSelectedStatus(opt.value)}
                data-testid={`status-btn-${opt.value.toLowerCase().replace(/ /g, '-')}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="tech-portal-section">
          <label className="tech-portal-section-label">COMMENTS</label>
          <textarea
            className="tech-portal-textarea"
            placeholder="Add any comments or notes about this service call..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={4}
            data-testid="tech-comments-input"
          />
        </div>

        <button
          className="tech-portal-btn primary"
          onClick={handleSubmit}
          data-testid="tech-submit-btn"
        >
          SUBMIT UPDATE
        </button>
      </div>
    </div>
  );
};

export default TechPortalPage;
